import { useState, useEffect, useMemo } from 'react';
import {
  Wallet,
  Receipt,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import {
  StatCardWithComparison,
  PartnerDetails,
  CategoryBreakdownChart,
  MonthlyTrendChart,
  RecentTransactionsList,
  SavingsRateCard,
  RecurringForecastCard,
  TopCategoriesCard,
  WidgetGrid,
  WidgetWrapper,
  BudgetProgressWidget,
  AccountCarousel,
} from './index';
import type { BudgetSummary, DashboardStats, CommonWidgetId, Category, SavingsRate } from '../../types';
import { COMMON_WIDGETS } from '../../types';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { useChartColors } from './useChartColors';
import { useThemeStore } from '../../store/themeStore';
import { useSettingsStore } from '../../store/settingsStore';
import EquilibreBar from './EquilibreBar';
import BalanceHero from './BalanceHero';
import FlowChart from './FlowChart';
import CategoryList from './CategoryList';

interface CommonDashboardSectionProps {
  summary: BudgetSummary | null;
  stats: DashboardStats | null;
  partner1Name: string;
  partner2Name: string;
  partnerSingular: string;
  formatCurrency: (amount: number) => string;
  isAnnualView?: boolean;
  isWeeklyView?: boolean;
  visibleWidgets?: CommonWidgetId[];
  // Paramètres de période pour le drill-down
  month?: number;
  year?: number;
  weekStart?: string;
  weekEnd?: string;
  // Catégories pour le widget budget
  expenseCategories?: Category[];
  // Comptes partagés pour le carousel
  sharedBankAccounts?: Array<{ id: string; name: string; color: string }>;
}

// Tous les widgets par défaut (si pas de config spécifiée)
const ALL_WIDGETS: CommonWidgetId[] = [
  'totalIncome',
  'totalExpenses',
  'balance',
  'savingsRate',
  'topCategories',
  'recurringForecast',
  'budgetProgress',
  'monthlyTrend',
  'categoryBreakdown',
  'recentTransactions',
  'partnerDetails',
];

// Helper pour obtenir la taille d'un widget
const getSize = (widgetId: CommonWidgetId) => {
  const widget = COMMON_WIDGETS.find(w => w.id === widgetId);
  return widget?.size || 'small';
};

// Widgets qui font partie du bloc stat cards groupé
const STAT_CARD_WIDGETS: CommonWidgetId[] = ['totalIncome', 'totalExpenses', 'balance'];

// Calcul du taux d'épargne côté frontend (réplique la logique backend)
function calculateSavingsRate(totalIncome: number, totalExpenses: number): SavingsRate {
  if (totalIncome === 0) {
    return { rate: 0, amount: 0, status: 'average' };
  }
  const savingsAmount = totalIncome - totalExpenses;
  const rate = (savingsAmount / totalIncome) * 100;

  let status: SavingsRate['status'];
  if (rate >= 20) status = 'excellent';
  else if (rate >= 10) status = 'good';
  else if (rate >= 0) status = 'average';
  else if (rate >= -10) status = 'low';
  else status = 'negative';

  return { rate: Math.round(rate * 10) / 10, amount: Math.round(savingsAmount * 100) / 100, status };
}

export default function CommonDashboardSection({
  summary,
  stats,
  partner1Name,
  partner2Name,
  partnerSingular,
  formatCurrency,
  isAnnualView = false,
  isWeeklyView = false,
  visibleWidgets = ALL_WIDGETS,
  month,
  year,
  weekStart,
  weekEnd,
  expenseCategories = [],
  sharedBankAccounts = [],
}: CommonDashboardSectionProps) {
  const { t } = useTranslation('dashboard');
  // Thème actif : la barre d'équilibre héros n'est montrée que pour Méridien
  // (invariant : les thèmes conservés restent inchangés).
  const themeId = useThemeStore((s) => s.themeId);
  const calculationMode = useSettingsStore((s) => s.settings?.calculationMode ?? 'proportional');
  // Helper pour vérifier si un widget est visible
  const isVisible = (widgetId: CommonWidgetId) => visibleWidgets.includes(widgetId);

  // Liste des comptes pour le carousel, filtrée par les comptes accessibles (prop)
  const accessibleIds = useMemo(() => new Set(sharedBankAccounts.map(a => a.id)), [sharedBankAccounts]);
  const accounts = useMemo(() => {
    const allAccounts = summary?.accounts?.map(a => ({ id: a.id, name: a.name, color: a.color })) || sharedBankAccounts;
    if (sharedBankAccounts.length > 0 && summary?.accounts) {
      return allAccounts.filter(a => accessibleIds.has(a.id));
    }
    return allAccounts;
  }, [summary?.accounts, sharedBankAccounts, accessibleIds]);

  // Afficher l'option "Tous les comptes" uniquement si plus d'un compte accessible
  const canShowAll = accounts.length > 1;
  const defaultIndex = canShowAll ? -1 : 0;

  // State carousel : index partagé entre stat cards + distribution + savingsRate
  const [statCardsAccountIndex, setStatCardsAccountIndex] = useState(defaultIndex);
  // State carousel : widgets individuels
  const [categoryAccountIndex, setCategoryAccountIndex] = useState(defaultIndex);
  const [transactionsAccountIndex, setTransactionsAccountIndex] = useState(defaultIndex);
  const [budgetAccountIndex, setBudgetAccountIndex] = useState(defaultIndex);
  const [trendAccountIndex, setTrendAccountIndex] = useState(defaultIndex);
  const [partnerAccountIndex, setPartnerAccountIndex] = useState(defaultIndex); // Partagé : expenseChart + partnerDetails
  const [topCategoriesAccountIndex, setTopCategoriesAccountIndex] = useState(defaultIndex);
  // Reset des index quand les données changent (changement de période)
  useEffect(() => {
    setStatCardsAccountIndex(defaultIndex);
    setCategoryAccountIndex(defaultIndex);
    setTransactionsAccountIndex(defaultIndex);
    setBudgetAccountIndex(defaultIndex);
    setTrendAccountIndex(defaultIndex);
    setPartnerAccountIndex(defaultIndex);
    setTopCategoriesAccountIndex(defaultIndex);
  }, [summary, stats, defaultIndex]);

  // Lookup par ID dans le summary backend (au lieu d'index direct)
  const findSummaryAccount = (index: number) => {
    if (index < 0 || index >= accounts.length) return null;
    const accountId = accounts[index].id;
    return summary?.accounts?.find(a => a.id === accountId) || null;
  };

  // Compte sélectionné pour les stat cards
  const statAccount = statCardsAccountIndex >= 0 ? findSummaryAccount(statCardsAccountIndex) : null;
  const statCredits = statAccount ? statAccount.totalCredits : (summary?.totalCredits || 0);
  const statExpenses = statAccount ? statAccount.totalExpenses : (summary?.totalExpenses || 0);
  const statBalance = statAccount
    ? statAccount.endingBalance
    : (summary?.endingBalance ?? summary?.balance ?? 0);
  const isPerAccount = statCardsAccountIndex >= 0;

  // Comparaison mois précédent per-account (dérivée de monthlyTrendByAccount)
  const perAccountComparison = useMemo(() => {
    if (statCardsAccountIndex < 0 || !stats) return null;
    const accountId = accounts[statCardsAccountIndex]?.id;
    const accountTrend = stats.monthlyTrendByAccount?.find(a => a.accountId === accountId);
    if (!accountTrend || accountTrend.trends.length < 2) return null;
    const current = accountTrend.trends[accountTrend.trends.length - 1];
    const previous = accountTrend.trends[accountTrend.trends.length - 2];
    const calc = (cur: number, prev: number) => ({
      change: cur - prev,
      changePercent: prev !== 0 ? ((cur - prev) / Math.abs(prev)) * 100 : (cur !== 0 ? 100 : 0),
    });
    return {
      income: calc(current.totalIncome, previous.totalIncome),
      expenses: calc(current.totalExpenses, previous.totalExpenses),
      balance: calc(current.totalIncome - current.totalExpenses, previous.totalIncome - previous.totalExpenses),
    };
  }, [statCardsAccountIndex, stats, accounts]);

  // Helpers pour les comparaisons (global ou per-account)
  const incomeChange = isPerAccount ? perAccountComparison?.income.change : stats?.monthComparison.income.change;
  const incomeChangePercent = isPerAccount ? perAccountComparison?.income.changePercent : stats?.monthComparison.income.changePercent;
  const expensesChange = isPerAccount ? perAccountComparison?.expenses.change : stats?.monthComparison.expenses.change;
  const expensesChangePercent = isPerAccount ? perAccountComparison?.expenses.changePercent : stats?.monthComparison.expenses.changePercent;
  const balanceChange = isPerAccount ? perAccountComparison?.balance.change : stats?.monthComparison.balance.change;
  const balanceChangePercent = isPerAccount ? perAccountComparison?.balance.changePercent : stats?.monthComparison.balance.changePercent;

  // Taux d'épargne selon l'index sélectionné
  const currentSavingsRate = useMemo(() => {
    if (!isPerAccount && stats) return stats.savingsRate;
    if (statAccount) return calculateSavingsRate(statAccount.totalCredits, statAccount.totalExpenses);
    return stats?.savingsRate || { rate: 0, amount: 0, status: 'average' as const };
  }, [isPerAccount, statAccount, stats]);

  // Données categoryBreakdown selon l'index
  const currentCategoryData = useMemo(() => {
    if (categoryAccountIndex === -1 || !stats) return stats?.categoryBreakdown || [];
    const accountData = stats.categoryBreakdownByAccount?.find(
      a => a.accountId === accounts[categoryAccountIndex]?.id
    );
    return accountData?.categories || [];
  }, [categoryAccountIndex, stats, accounts]);

  // Données topCategories selon l'index
  const currentTopCategories = useMemo(() => {
    if (topCategoriesAccountIndex === -1 || !stats) return stats?.topCategories || [];
    const accountData = stats.categoryBreakdownByAccount?.find(
      a => a.accountId === accounts[topCategoriesAccountIndex]?.id
    );
    return (accountData?.categories || []).slice(0, 3);
  }, [topCategoriesAccountIndex, stats, accounts]);

  // Données recentTransactions filtrées
  const currentTransactions = useMemo(() => {
    if (transactionsAccountIndex === -1 || !stats) return stats?.recentTransactions || [];
    const accountName = accounts[transactionsAccountIndex]?.name;
    return stats.recentTransactions.filter(t => t.sharedBankAccountName === accountName);
  }, [transactionsAccountIndex, stats, accounts]);

  // Données monthlyTrend selon l'index
  const currentTrendData = useMemo(() => {
    if (trendAccountIndex === -1 || !stats) return stats?.monthlyTrend || [];
    const accountData = stats.monthlyTrendByAccount?.find(
      a => a.accountId === accounts[trendAccountIndex]?.id
    );
    return accountData?.trends || [];
  }, [trendAccountIndex, stats, accounts]);

  // Compte sélectionné pour les widgets partenaires
  const partnerAccount = partnerAccountIndex >= 0 ? findSummaryAccount(partnerAccountIndex) : null;

  // Date de début pour le solde cumulatif (si un compte spécifique est sélectionné)
  const cumulativeStartDate = partnerAccount?.startDate ?? null;

  // Couleurs partenaires dérivées du thème (partenaire 1 = primary, partenaire 2 = secondary)
  const chartColors = useChartColors();

  // partnersData pour partnerDetails
  const partnersData = useMemo(() => {
    const src = partnerAccount || summary;
    if (!src) return [];
    return [
      {
        name: partner1Name,
        percentage: partnerAccount ? partnerAccount.partner1Percentage : (summary?.partner1Percentage || 0),
        income: summary?.partner1Income || 0,
        share: partnerAccount ? partnerAccount.partner1Share : (summary?.partner1Share || 0),
        color: chartColors.primary,
        contributions: partnerAccount ? partnerAccount.partner1Contributions : summary?.partner1Contributions,
        cumulativeBalance: partnerAccount ? partnerAccount.partner1CumulativeBalance : summary?.partner1CumulativeBalance,
      },
      {
        name: partner2Name,
        percentage: partnerAccount ? partnerAccount.partner2Percentage : (summary?.partner2Percentage || 0),
        income: summary?.partner2Income || 0,
        share: partnerAccount ? partnerAccount.partner2Share : (summary?.partner2Share || 0),
        color: chartColors.secondary,
        contributions: partnerAccount ? partnerAccount.partner2Contributions : summary?.partner2Contributions,
        cumulativeBalance: partnerAccount ? partnerAccount.partner2CumulativeBalance : summary?.partner2CumulativeBalance,
      },
    ];
  }, [partnerAccount, summary, partner1Name, partner2Name, chartColors]);

  // Carousel éléments réutilisables
  const hasMultipleAccounts = accounts.length > 1;

  // Widgets stat cards visibles (pour le bloc groupé)
  const visibleStatCards = STAT_CARD_WIDGETS.filter(w => isVisible(w));

  // Vérifier s'il y a au moins un widget visible
  const hasAnyWidget = visibleWidgets.length > 0;

  if (!hasAnyWidget) {
    return null;
  }

  // Rendu d'un stat card individuel (utilisé dans le bloc groupé et en standalone)
  const renderStatCard = (widgetId: CommonWidgetId) => {
    switch (widgetId) {
      case 'totalIncome':
        return (
          <div key={widgetId} className="card">
            <StatCardWithComparison
              icon={<Wallet className="w-6 h-6 text-green-600" />}
              iconBgColor="bg-green-100"
              label={t('common.credits')}
              value={formatCurrency(statCredits)}
              change={incomeChange}
              changePercent={incomeChangePercent}
              noCard
            />
            {!isPerAccount && summary?.totalIncome !== undefined && (
              <p className="text-xs text-gray-500 mt-1 pl-1">
                {t('common.coupleIncome')} : {formatCurrency(summary.totalIncome)}
              </p>
            )}
          </div>
        );
      case 'totalExpenses':
        return (
          <StatCardWithComparison
            key={widgetId}
            icon={<Receipt className="w-6 h-6 text-red-600" />}
            iconBgColor="bg-red-100"
            label={t('common.totalExpenses')}
            value={formatCurrency(statExpenses)}
            change={expensesChange}
            changePercent={expensesChangePercent}
            invertColors
          />
        );
      case 'balance':
        return (
          <StatCardWithComparison
            key={widgetId}
            icon={
              statBalance >= 0 ? (
                <TrendingUp className="w-6 h-6 text-green-600" />
              ) : (
                <TrendingDown className="w-6 h-6 text-red-600" />
              )
            }
            iconBgColor={statBalance >= 0 ? 'bg-green-100' : 'bg-red-100'}
            label={t('common.balance')}
            value={formatCurrency(statBalance)}
            valueColor={statBalance >= 0 ? 'text-green-600' : 'text-red-600'}
            change={balanceChange}
            changePercent={balanceChangePercent}
          />
        );
      default:
        return null;
    }
  };

  // ─── Hero « Méridien » ───────────────────────────────────────────────
  // Sur Méridien, les 4 stat-cards identiques, le camembert et la courbe
  // Recharts sont remplacés par une hiérarchie : un solde dominant + 3 KPI,
  // l'équilibre en panneau compact, le flux en aire, les catégories en liste.
  const isMeridian = themeId === 'meridian';

  // Séries des micro-courbes, dérivées de la tendance mensuelle réelle.
  // `currentTrendData` (et non `stats.monthlyTrend`) : les courbes doivent suivre
  // le compte sélectionné, exactement comme les chiffres au-dessus d'elles.
  const kpiSeries = useMemo(() => {
    const credits = currentTrendData.map((m) => m.totalIncome);
    const debits = currentTrendData.map((m) => m.totalExpenses);
    const savings = currentTrendData.map((m) => (m.totalIncome > 0 ? ((m.totalIncome - m.totalExpenses) / m.totalIncome) * 100 : 0));
    // Les libellés datent chaque point : sans eux, l'infobulle des sparklines
    // afficherait un montant sans repère temporel (et reste donc désactivée).
    const labels = currentTrendData.map((m) => m.label);
    return { credits, debits, savings, labels };
  }, [currentTrendData]);

  const flowData = useMemo(
    () => currentTrendData.map((m) => ({ label: m.label, income: m.totalIncome, expenses: m.totalExpenses, balance: m.balance })),
    [currentTrendData],
  );

  // `CategoryList` consomme désormais le CategoryBreakdown complet : il a besoin
  // de categoryId (drill-down) et categoryIsDefault (traduction) — les projeter
  // sur {name, amount, percentage} cassait le détail par sous-catégorie.

  const showEquilibre = !!summary && summary.partner1Percentage + summary.partner2Percentage > 0;

  // Sélecteur de compte UNIQUE : remplace les carrousels par widget, qui
  // mangeaient une ligne entière chacun. Il pilote tous les blocs d'un coup.
  const handleGlobalAccountChange = (index: number) => {
    setStatCardsAccountIndex(index);
    setCategoryAccountIndex(index);
    setTransactionsAccountIndex(index);
    setBudgetAccountIndex(index);
    setTrendAccountIndex(index);
    setPartnerAccountIndex(index);
    setTopCategoriesAccountIndex(index);
  };

  const accountSelector = hasMultipleAccounts ? (
    <select
      value={statCardsAccountIndex}
      onChange={(e) => handleGlobalAccountChange(Number(e.target.value))}
      aria-label={t('accountCarousel.account')}
      className="chip cursor-pointer"
      style={{ paddingRight: 26 }}
    >
      {canShowAll && <option value={-1}>{t('accountCarousel.allAccounts')}</option>}
      {accounts.map((a, i) => (
        <option key={a.id} value={i}>{a.name}</option>
      ))}
    </select>
  ) : undefined;

  // Le graphe de flux reste masqué en vue hebdomadaire (une tendance à 6 mois
  // n'a pas de sens à côté d'une semaine), comme dans l'ancien dashboard.
  const showTrendPanel = isVisible('monthlyTrend') && !!stats && !isWeeklyView && flowData.length >= 2;
  const showCategoryPanel = isVisible('categoryBreakdown') && !!stats;

  if (isMeridian) {
    return (
      <div className="flex flex-col gap-[22px]">
        {/* HERO : solde + KPI | équilibre */}
        <section className="grid grid-cols-1 lg:grid-cols-[1.15fr_.85fr] gap-[22px] pt-2">
          <BalanceHero
            label={t('common.balance')}
            balance={statBalance}
            changePercent={balanceChangePercent}
            change={balanceChange}
            previousLabel={stats?.monthlyTrend?.[stats.monthlyTrend.length - 2]?.label}
            credits={statCredits}
            debits={statExpenses}
            savingsRate={currentSavingsRate.rate}
            series={kpiSeries}
            formatCurrency={formatCurrency}
            accountSelector={accountSelector}
          />
          {showEquilibre && summary && (
            <EquilibreBar
              partner1Name={partner1Name}
              partner2Name={partner2Name}
              partner1Percentage={summary.partner1Percentage}
              partner2Percentage={summary.partner2Percentage}
              partner1Share={summary.partner1Share}
              partner2Share={summary.partner2Share}
              partner1Income={summary.partner1Income}
              partner2Income={summary.partner2Income}
              calculationMode={calculationMode}
              formatCurrency={formatCurrency}
            />
          )}
        </section>

        {/* MID : flux 6 mois | catégories en liste — le paramétrage widget
            reste maître de l'affichage de chacun des deux panneaux. */}
        {(showTrendPanel || showCategoryPanel) && (
          <section
            className={clsx(
              'grid grid-cols-1 gap-[22px]',
              showTrendPanel && showCategoryPanel && 'lg:grid-cols-[1.55fr_1fr]',
            )}
          >
            {showTrendPanel && (
              <div className="panel">
                <div className="panel-h">
                  <h3>{t('charts.monthlyTrend')}</h3>
                  <div className="chart-legend">
                    <span className="lg"><i style={{ background: 'var(--pa)' }} />{t('common.credits')}</span>
                    <span className="lg"><i style={{ background: 'var(--text-3)' }} />{t('common.totalExpenses')}</span>
                    <span className="lg"><i style={{ background: 'var(--chart-balance)' }} />{t('common.balance')}</span>
                  </div>
                </div>
                <FlowChart data={flowData} formatCurrency={formatCurrency} />
              </div>
            )}

            {showCategoryPanel && (
              <div className="panel">
                <div className="panel-h">
                  <h3>{t('charts.categoryBreakdown')}</h3>
                  <span className="hint">{t('categoryBreakdownDetails.clickToDrill')}</span>
                </div>
                <CategoryList
                  categories={currentCategoryData}
                  formatCurrency={formatCurrency}
                  month={month}
                  year={year}
                  weekStart={weekStart}
                  weekEnd={weekEnd}
                  isPersonal={false}
                />
              </div>
            )}
          </section>
        )}

        {/* Widgets restants — la configuration par widget reste maîtresse de
            l'affichage de chacun d'eux, y compris ceux dont l'information est
            aussi résumée dans le héros (taux d'épargne, répartition). */}
        <WidgetGrid>
          {isVisible('savingsRate') && stats && (
            <WidgetWrapper size={getSize('savingsRate')}>
              <SavingsRateCard savingsRate={currentSavingsRate} formatCurrency={formatCurrency} />
            </WidgetWrapper>
          )}
          {isVisible('topCategories') && stats && (
            <WidgetWrapper size={getSize('topCategories')}>
              <TopCategoriesCard categories={currentTopCategories} formatCurrency={formatCurrency} />
            </WidgetWrapper>
          )}
          {isVisible('budgetProgress') && (
            <WidgetWrapper size={getSize('budgetProgress')}>
              <BudgetProgressWidget
                type="shared"
                formatCurrency={formatCurrency}
                month={month}
                year={year}
                weekStart={weekStart}
                weekEnd={weekEnd}
                expenseCategories={expenseCategories}
                bankAccounts={accounts}
                filterBankAccountId={budgetAccountIndex >= 0 ? accounts[budgetAccountIndex]?.id : undefined}
              />
            </WidgetWrapper>
          )}
          {isVisible('recurringForecast') && stats && !isAnnualView && !isWeeklyView && (
            <WidgetWrapper size={getSize('recurringForecast')}>
              <RecurringForecastCard forecast={stats.recurringForecast} formatCurrency={formatCurrency} />
            </WidgetWrapper>
          )}
          {isVisible('recentTransactions') && stats && (
            <WidgetWrapper size={getSize('recentTransactions')}>
              <RecentTransactionsList transactions={currentTransactions} formatCurrency={formatCurrency} />
            </WidgetWrapper>
          )}
          {isVisible('partnerDetails') && (
            <WidgetWrapper size={getSize('partnerDetails')}>
              <PartnerDetails
                title={t('common.detailsBy', { partner: partnerSingular })}
                partners={partnersData}
                formatCurrency={formatCurrency}
                cumulativeStartDate={cumulativeStartDate}
              />
            </WidgetWrapper>
          )}
        </WidgetGrid>
      </div>
    );
  }

  return (
    <>
      <WidgetGrid>
      {/* Bloc stat cards groupé (quand multi-comptes) */}
      {hasMultipleAccounts && visibleStatCards.length > 0 && (
        <WidgetWrapper size="large">
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-3">
            <AccountCarousel
              accounts={accounts}
              currentIndex={statCardsAccountIndex}
              onIndexChange={setStatCardsAccountIndex}
              showAllOption={canShowAll}
              compact
            />
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${visibleStatCards.length > 2 ? 'lg:grid-cols-4' : 'lg:grid-cols-2'} gap-3 mt-3`}>
              {visibleStatCards.map(renderStatCard)}
            </div>
          </div>
        </WidgetWrapper>
      )}

      {/* Stat cards individuels (quand un seul compte) */}
      {!hasMultipleAccounts && (
        <>
          {isVisible('totalIncome') && (
            <WidgetWrapper size={getSize('totalIncome')}>
              <div className="card">
                <StatCardWithComparison
                  icon={<Wallet className="w-6 h-6 text-green-600" />}
                  iconBgColor="bg-green-100"
                  label={t('common.credits')}
                  value={formatCurrency(statCredits)}
                  change={stats?.monthComparison.income.change}
                  changePercent={stats?.monthComparison.income.changePercent}
                  noCard
                />
                {summary?.totalIncome !== undefined && (
                  <p className="text-xs text-gray-500 mt-1 pl-1">
                    {t('common.coupleIncome')} : {formatCurrency(summary.totalIncome)}
                  </p>
                )}
              </div>
            </WidgetWrapper>
          )}

          {isVisible('totalExpenses') && (
            <WidgetWrapper size={getSize('totalExpenses')}>
              <StatCardWithComparison
                icon={<Receipt className="w-6 h-6 text-red-600" />}
                iconBgColor="bg-red-100"
                label={t('common.totalExpenses')}
                value={formatCurrency(statExpenses)}
                change={stats?.monthComparison.expenses.change}
                changePercent={stats?.monthComparison.expenses.changePercent}
                invertColors
              />
            </WidgetWrapper>
          )}

          {isVisible('balance') && (
            <WidgetWrapper size={getSize('balance')}>
              <StatCardWithComparison
                icon={
                  statBalance >= 0 ? (
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  ) : (
                    <TrendingDown className="w-6 h-6 text-red-600" />
                  )
                }
                iconBgColor={statBalance >= 0 ? 'bg-green-100' : 'bg-red-100'}
                label={t('common.balance')}
                value={formatCurrency(statBalance)}
                valueColor={statBalance >= 0 ? 'text-green-600' : 'text-red-600'}
                change={stats?.monthComparison.balance.change}
                changePercent={stats?.monthComparison.balance.changePercent}
              />
            </WidgetWrapper>
          )}

        </>
      )}

      {/* Taux d'épargne */}
      {isVisible('savingsRate') && stats && (
        <WidgetWrapper size={getSize('savingsRate')}>
          <SavingsRateCard
            savingsRate={currentSavingsRate}
            formatCurrency={formatCurrency}
          />
        </WidgetWrapper>
      )}

      {/* Top catégories */}
      {isVisible('topCategories') && stats && (
        <WidgetWrapper size={getSize('topCategories')}>
          {hasMultipleAccounts ? (
            <div className="card h-full">
              <AccountCarousel
                accounts={accounts}
                currentIndex={topCategoriesAccountIndex}
                onIndexChange={setTopCategoriesAccountIndex}
                showAllOption={canShowAll}
                compact
              />
              <TopCategoriesCard
                categories={currentTopCategories}
                formatCurrency={formatCurrency}
                noCard
              />
            </div>
          ) : (
            <TopCategoriesCard
              categories={stats.topCategories}
              formatCurrency={formatCurrency}
            />
          )}
        </WidgetWrapper>
      )}

      {/* Prévisions récurrentes (masqué en vue annuelle et hebdomadaire) */}
      {isVisible('recurringForecast') && stats && !isAnnualView && !isWeeklyView && (
        <WidgetWrapper size={getSize('recurringForecast')}>
          <RecurringForecastCard
            forecast={stats.recurringForecast}
            formatCurrency={formatCurrency}
          />
        </WidgetWrapper>
      )}

      {/* Budgets prévisionnels */}
      {isVisible('budgetProgress') && (
        <WidgetWrapper size={getSize('budgetProgress')}>
          <BudgetProgressWidget
            type="shared"
            formatCurrency={formatCurrency}
            month={month}
            year={year}
            weekStart={weekStart}
            weekEnd={weekEnd}
            expenseCategories={expenseCategories}
            bankAccounts={accounts}
            filterBankAccountId={budgetAccountIndex >= 0 ? accounts[budgetAccountIndex]?.id : undefined}
            carouselElement={hasMultipleAccounts ? (
              <AccountCarousel
                accounts={accounts}
                currentIndex={budgetAccountIndex}
                onIndexChange={setBudgetAccountIndex}
                showAllOption={canShowAll}
              />
            ) : undefined}
          />
        </WidgetWrapper>
      )}

      {/* Tendance mensuelle (masqué en vue hebdomadaire) */}
      {isVisible('monthlyTrend') && stats && !isWeeklyView && (
        <WidgetWrapper size={getSize('monthlyTrend')}>
          {hasMultipleAccounts ? (
            <div className="card h-full">
              <AccountCarousel
                accounts={accounts}
                currentIndex={trendAccountIndex}
                onIndexChange={setTrendAccountIndex}
                showAllOption={canShowAll}
              />
              <MonthlyTrendChart
                data={currentTrendData}
                formatCurrency={formatCurrency}
                noCard
              />
            </div>
          ) : (
            <MonthlyTrendChart
              data={stats.monthlyTrend}
              formatCurrency={formatCurrency}
            />
          )}
        </WidgetWrapper>
      )}

      {/* Dépenses par catégorie */}
      {isVisible('categoryBreakdown') && stats && (
        <WidgetWrapper size={getSize('categoryBreakdown')}>
          {hasMultipleAccounts ? (
            <div className="card h-full">
              <AccountCarousel
                accounts={accounts}
                currentIndex={categoryAccountIndex}
                onIndexChange={setCategoryAccountIndex}
                showAllOption={canShowAll}
              />
              <CategoryBreakdownChart
                data={currentCategoryData}
                formatCurrency={formatCurrency}
                month={month}
                year={year}
                weekStart={weekStart}
                weekEnd={weekEnd}
                isPersonal={false}
                noCard
              />
            </div>
          ) : (
            <CategoryBreakdownChart
              data={stats.categoryBreakdown}
              formatCurrency={formatCurrency}
              month={month}
              year={year}
              weekStart={weekStart}
              weekEnd={weekEnd}
              isPersonal={false}
            />
          )}
        </WidgetWrapper>
      )}

      {/* Transactions récentes */}
      {isVisible('recentTransactions') && stats && (
        <WidgetWrapper size={getSize('recentTransactions')}>
          {hasMultipleAccounts ? (
            <div className="card h-full">
              <AccountCarousel
                accounts={accounts}
                currentIndex={transactionsAccountIndex}
                onIndexChange={setTransactionsAccountIndex}
                showAllOption={canShowAll}
              />
              <RecentTransactionsList
                transactions={currentTransactions}
                formatCurrency={formatCurrency}
                noCard
              />
            </div>
          ) : (
            <RecentTransactionsList
              transactions={stats.recentTransactions}
              formatCurrency={formatCurrency}
            />
          )}
        </WidgetWrapper>
      )}

      {/* Détails par partenaire */}
      {isVisible('partnerDetails') && (
        <WidgetWrapper size={getSize('partnerDetails')}>
          {hasMultipleAccounts ? (
            <div className="card h-full">
              <AccountCarousel
                accounts={accounts}
                currentIndex={partnerAccountIndex}
                onIndexChange={setPartnerAccountIndex}
                showAllOption={canShowAll}
              />
              <PartnerDetails
                title={t('common.detailsBy', { partner: partnerSingular })}
                partners={partnersData}
                formatCurrency={formatCurrency}
                noCard
                cumulativeStartDate={cumulativeStartDate}
              />
            </div>
          ) : (
            <PartnerDetails
              title={t('common.detailsBy', { partner: partnerSingular })}
              partners={partnersData}
              formatCurrency={formatCurrency}
              cumulativeStartDate={cumulativeStartDate}
            />
          )}
        </WidgetWrapper>
      )}
      </WidgetGrid>
    </>
  );
}
